import { ChangeDetectorRef, Component, DestroyRef, inject, Injector, input, model, OnInit, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { IconModule, RadioOptionModule, SimpleInputModule, TextAreaComponentModule } from '@hglobal/fin-ui';
import { FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';

import { CopyTooltipComponent, SearchSelectComponent } from '@kr/components';
import { decimalLengthValidator, WalletItemComponent } from '@kr/components/exchange/components';
import { ExchangeFormSepaSwiftModel } from '@kr/components/exchange/models/exchange-form-sepa-swift.model';
import { WalletCard } from '@kr/models';
import { validateTransferMinAmount } from '@kr/validators';

import { digitPattern } from '../../../../core/helpers/digit-pattern';

@Component({
  selector: 'kr-exchange-set-crypto-fiat-amount',
  imports: [
    RadioOptionModule,
    ReactiveFormsModule,
    CopyTooltipComponent,
    TranslateModule,
    SearchSelectComponent,
    WalletItemComponent,
    TextAreaComponentModule,
    SimpleInputModule,
    IconModule,
  ],
  templateUrl: './exchange-set-crypto-fiat-amount.component.html',
  styleUrl: './exchange-set-crypto-fiat-amount.component.scss',
})
export class ExchangeSetCryptoFiatAmountComponent implements OnInit {
  readonly #cd = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);

  wallet = model<WalletCard>();
  wallets = input<WalletCard[]>();

  form = input<FormGroup<GroupResolverFormBuilder<ExchangeFormSepaSwiftModel>>>();
  transferType = {
    sepa: { value: 'sepa', label: 'SEPA' },
    swift: { value: 'swift', label: 'SWIFT' },
  };

  get walletTransferMinAmount(): number {
    const amount = this.wallet().coin?.minAmount;

    return Number(amount);
  }

  get walletTransferMaxDecimals(): number {
    const amount = this.wallet().coin?.decimals;

    return Number(amount);
  }

  validateTransferAmount(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const invalidBalance = +this.wallet()?.balanceAvailable < +control.value;

      return invalidBalance ? { invalidBalance: { value: control.value } } : null;
    };
  }

  ngOnInit(): void {
    this.#setAmountValidators();

    runInInjectionContext(this.#injector, () => {
      this.form()
        .controls.walletFromId.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(walletId => {
          this.wallet.set(this.wallets().find(wallet => wallet.id === walletId));
          this.form().controls.amount.updateValueAndValidity();
        });
    });
  }

  #setAmountValidators(): void {
    this.form().controls.amount.addValidators([
      validateTransferMinAmount(this.walletTransferMinAmount.toString()),
      Validators.pattern(digitPattern),
      this.validateTransferAmount(),
      () => decimalLengthValidator(this.form().controls.amount, this.wallet()),
    ]);

    this.form().controls.amountReceive.addValidators([Validators.pattern(digitPattern)]);

    this.form().updateValueAndValidity();
    this.#cd.detectChanges();
  }
}
